import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpen, FileText, Images } from "lucide-react";
import { SectionHead, EASE } from "./SectionHead";
import { thumb } from "@/lib/api";

// Cover drawn as a physical book: spine shading on the left, stacked page edges on the right. Artwork keeps its own ratio.
const Book = ({ comic, size = "lg" }) => (
  <span className={`relative inline-block ${size === "lg" ? "comic-book" : "comic-book-sm"}`}>
    <img src={thumb(comic.cover_url, size === "lg" ? 900 : 480)} alt={comic.title} loading="lazy"
      className={`block w-auto object-contain bg-[#12131A] ${size === "lg" ? "max-h-[52vh] md:max-h-[480px] max-w-full" : "h-32 md:h-40 max-w-[120px]"}`} />
    <span className="absolute inset-y-0 left-0 w-[7%] bg-gradient-to-r from-black/60 via-white/10 to-transparent pointer-events-none" />
  </span>
);

const Meta = ({ comic }) => (
  <p className="font-mono text-[11px] tracking-[0.2em] text-zinc-300 flex flex-wrap items-center gap-3">
    {comic.year && <span>{comic.year}</span>}
    {comic.pdf_url && <span className="flex items-center gap-1 text-[#F5A524]"><FileText size={12} /> FLIPBOOK</span>}
    {comic.images?.length > 0 && <span className="flex items-center gap-1 text-[#F5A524]"><Images size={12} /> {comic.images.length + 1} PAGES</span>}
  </p>
);

const Featured = ({ comic, n }) => (
  <motion.div key={comic.id} initial={{ opacity: 0.4, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.5, ease: EASE }}
    className="grid md:grid-cols-[auto_1fr] gap-6 md:gap-12 items-center" data-testid="comics-featured">
    <Link to={`/project/${comic.id}`} data-testid={`comics-featured-cover-${comic.id}`} className="group justify-self-center md:justify-self-start transition-transform duration-500 hover:-rotate-1 hover:-translate-y-1">
      <Book comic={comic} />
    </Link>
    <div className="max-w-xl">
      <p className="font-mono text-xs tracking-[0.3em] text-[#F5A524]">ISSUE #{String(n).padStart(2, "0")}</p>
      <h3 data-testid="comics-featured-title" className="mt-2 font-display font-black uppercase text-4xl sm:text-6xl leading-[0.9]">{comic.title}</h3>
      <div className="mt-3"><Meta comic={comic} /></div>
      {comic.description && <p className="mt-4 text-base text-zinc-300 leading-relaxed line-clamp-4">{comic.description}</p>}
      <Link to={`/project/${comic.id}`} data-testid="comics-read-btn"
        className="mt-6 inline-flex items-center gap-3 clip-tab bg-[#F5A524] text-black px-6 py-3.5 font-mono text-xs font-bold tracking-[0.25em] hover:bg-white transition-colors">
        <BookOpen size={16} /> READ NOW
      </Link>
    </div>
  </motion.div>
);

export const Comics = ({ index, comics }) => {
  const [sel, setSel] = useState(0);
  const cur = comics[Math.min(sel, comics.length - 1)];

  return (
    <section id="comics" data-testid="comics-section" className="relative px-5 sm:px-10 lg:px-16 py-16 md:py-24 border-t border-[#F5A524]/15 overflow-hidden">
      <div aria-hidden="true" className="absolute right-[-4vw] top-10 font-display font-black uppercase text-[22vw] leading-none text-white/[0.03] select-none pointer-events-none">INK</div>
      <SectionHead index={index} kicker="COMICS" title={<>Read the<br /><span className="text-[#F5A524]">Issues</span></>}>
        <p className="max-w-sm text-sm text-zinc-400">Full comics, readable right here — tap a cover to open the flipbook reader.</p>
      </SectionHead>
      <div className="relative">
        <AnimatePresence mode="popLayout" initial={false}><Featured key={cur.id} comic={cur} n={sel + 1} /></AnimatePresence>
      </div>
      {comics.length > 1 && (
        <div className="mt-10 md:mt-14">
          <p className="mb-3 font-mono text-[11px] tracking-[0.3em] text-zinc-400">// THE SHELF — {String(comics.length).padStart(2, "0")} ISSUES</p>
          <div data-testid="comics-shelf" className="flex items-end gap-4 md:gap-6 overflow-x-auto no-scrollbar pb-4 border-b-4 border-[#F5A524]/40 -mx-5 px-5 md:mx-0 md:px-0">
            {comics.map((c, i) => (
              <button key={c.id} type="button" data-testid={`comics-shelf-item-${c.id}`} onClick={() => setSel(i)} aria-pressed={i === sel} aria-label={`Show ${c.title}`}
                className={`shrink-0 text-left transition-[transform,opacity] duration-300 ${i === sel ? "-translate-y-3" : "opacity-60 hover:opacity-100 hover:-translate-y-1"}`}>
                <Book comic={c} size="sm" />
                <span className={`mt-2 block max-w-[120px] truncate font-mono text-[10px] tracking-[0.2em] ${i === sel ? "text-[#F5A524]" : "text-zinc-400"}`}>{c.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
